// top-customers-by-tickets-table.tsx — Top customers by ticket volume, DataTable.
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { TableLink } from '@/components/table-link';
import type { TopCustomerByTickets } from '@/api/tickets-statistics';

const COLUMNS: ColumnDef<TopCustomerByTickets, unknown>[] = [
  {
    id: 'customer_name',
    accessorKey: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => (
      <TableLink to={`/customers/${row.original.customer_id}`} truncate>
        {row.original.company_name || row.original.customer_name}
      </TableLink>
    ),
  },
  {
    id: 'total_tickets',
    accessorKey: 'total_tickets',
    header: 'Total Tickets',
    cell: ({ row }) => row.original.total_tickets.toLocaleString('en-US'),
  },
];

interface Props {
  data: TopCustomerByTickets[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function TopCustomersByTicketsTable({ data, isLoading, isError }: Props) {
  const rows = data ?? [];

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Top Customers by Ticket Volume</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load customer data" />
        ) : isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 36 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={COLUMNS}
            data={rows}
            getRowId={(r) => String(r.customer_id)}
            emptyState={
              <EmptyState
                title="No customer data"
                description="No customers with tickets in this date range."
              />
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
